import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Alert, Typography, message } from 'antd';
import { EnvironmentOutlined, AimOutlined } from '@ant-design/icons';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import 'leaflet/dist/leaflet.css';

// Fix default Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const { Text } = Typography;
const DEFAULT_CENTER = [22.9734, 78.6569]; // India center

async function reverseGeocode(lat, lng) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
      { headers: { 'Accept-Language': 'en', 'User-Agent': 'KhetiHisaab/1.0' } }
    );
    const data = await res.json();
    const a = data.address || {};
    return a.village || a.town || a.city || a.county || a.state || data.display_name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  } catch {
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  }
}

function MapPicker({ onPick }) {
  useMapEvents({
    click(e) { onPick(e.latlng); },
  });
  return null;
}

function FlyToPin({ pin }) {
  const map = useMap();
  useEffect(() => {
    if (pin) map.flyTo([pin.lat, pin.lng], 14, { animate: true, duration: 1 });
  }, [pin, map]);
  return null;
}

function LocateButton({ onLocate }) {
  const map = useMap();
  const [locating, setLocating] = useState(false);

  const handleLocate = () => {
    if (!navigator.geolocation) {
      message.error('Geolocation not supported by your browser');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latlng = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        map.flyTo([latlng.lat, latlng.lng], 15, { animate: true });
        onLocate(latlng);
        setLocating(false);
      },
      () => {
        message.error('Unable to get your location');
        setLocating(false);
      },
      { timeout: 10000 }
    );
  };

  return (
    <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 1000 }}>
      <Button
        icon={<AimOutlined />}
        loading={locating}
        onClick={handleLocate}
        style={{ background: '#fff', borderColor: '#d9d9d9', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}
        title="Find my location"
      />
    </div>
  );
}

export default function FarmModal({ open, onClose, onSubmit, initialValues, loading }) {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [pin, setPin] = useState(null);          // { lat, lng }
  const [flyTarget, setFlyTarget] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [geocoding, setGeocoding] = useState(false);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ name: initialValues?.name || '' });
      const parts = initialValues?.location?.split(',');
      if (parts?.length === 2) {
        const latlng = { lat: parseFloat(parts[0]), lng: parseFloat(parts[1]) };
        if (!isNaN(latlng.lat) && !isNaN(latlng.lng)) {
          setPin(latlng);
          setFlyTarget(latlng);
        } else {
          setPin(null);
          setFlyTarget(null);
        }
      } else {
        setPin(null);
        setFlyTarget(null);
      }
      setLocationName('');
    } else {
      form.resetFields();
      setPin(null);
      setFlyTarget(null);
      setLocationName('');
    }
  }, [open, initialValues, form]);

  const handleMapPick = async (latlng) => {
    setPin(latlng);
    setGeocoding(true);
    const name = await reverseGeocode(latlng.lat, latlng.lng);
    setLocationName(name);
    setGeocoding(false);
  };

  const handleOk = () => {
    if (!pin) {
      form.setFields([{ name: 'name', errors: [] }]);
      // show inline message instead of blocking submit
    }
    form.validateFields().then((values) => {
      onSubmit({
        farm: values.name,
        location: pin ? `${pin.lat}, ${pin.lng}` : '',
      });
    }).catch(() => {});
  };

  return (
    <Modal
      title={initialValues?.id ? t('edit_farm') : t('add_farm')}
      open={open}
      onCancel={onClose}
      width={680}
      footer={[
        <Button key="cancel" onClick={onClose}>{t('cancel')}</Button>,
        <Button key="ok" type="primary" loading={loading} onClick={handleOk}
          style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>{t('save')}</Button>,
      ]}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 20 }}
        message="A Farm is a location where your plots (land) are situated. Add your farm first, then add plots inside it."
      />

      <Form form={form} layout="vertical" size="large">
        <Form.Item label={t('farm_name')} name="name" rules={[{ required: true, message: 'Enter farm name' }]}>
          <Input placeholder="e.g. Home Farm, North Field Farm" />
        </Form.Item>

        <Form.Item label="Location" required>
          <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
            <MapContainer
              center={pin ? [pin.lat, pin.lng] : DEFAULT_CENTER}
              zoom={pin ? 14 : 5}
              style={{ height: 300, width: '100%', position: 'relative' }}
              scrollWheelZoom
            >
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution="Esri"
              />
              <FlyToPin pin={flyTarget} />
              <MapPicker onPick={handleMapPick} />
              <LocateButton onLocate={handleMapPick} />
              {pin && <Marker position={[pin.lat, pin.lng]} />}
            </MapContainer>
          </div>

          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <EnvironmentOutlined style={{ color: pin ? '#1a6b2a' : '#bfbfbf' }} />
            {pin ? (
              <Text style={{ color: '#1a6b2a' }}>
                {geocoding ? 'Finding location name...' : locationName || `${pin.lat.toFixed(4)}, ${pin.lng.toFixed(4)}`}
                <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                  ({pin.lat.toFixed(5)}, {pin.lng.toFixed(5)})
                </Text>
              </Text>
            ) : (
              <Text type="secondary">Tap on the map to pin your farm location</Text>
            )}
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}
